function ok() { return { ok: true }; }
function fail(field, message) {
    return { ok: false, errors: [{ field, message }] };
}
function parseNumber(input) {
    if (input === null || input === undefined)
        return null;
    const n = Number(String(input));
    return Number.isFinite(n) ? n : null;
}
function formatWithSeparators(num, decimals, thousandSep, decimalSep) {
    const fixed = num.toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    const withThousand = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    return decPart !== undefined ? `${withThousand}${decimalSep}${decPart}` : withThousand;
}
const numberFormat = {
    id: 'number_format',
    family: 'number',
    label: 'Number format',
    description: 'Format a number with thousand and decimal separators.',
    defaultConfig: { decimals: 2, thousandSep: ',', decimalSep: '.' },
    evaluate: (input, config) => {
        const c = config;
        const n = parseNumber(input);
        if (n === null)
            return input === null || input === undefined ? '' : String(input);
        return formatWithSeparators(n, c.decimals, c.thousandSep ?? ',', c.decimalSep ?? '.');
    },
    validate: (config) => {
        const c = config;
        if (typeof c.decimals !== 'number' || c.decimals < 0)
            return fail('decimals', 'decimals must be a non-negative integer');
        return ok();
    },
    formSchema: [
        { key: 'decimals', label: 'Decimal places', type: 'number', required: true },
        { key: 'thousandSep', label: 'Thousand separator', type: 'text' },
        { key: 'decimalSep', label: 'Decimal separator', type: 'text' },
    ],
    sampleSeeds: ['1234567.89', '0.5', '-42.1'],
};
const currencyFormat = {
    id: 'currency_format',
    family: 'number',
    label: 'Currency format',
    description: 'Format a number with currency symbol, prefix or suffix.',
    defaultConfig: { symbol: '$', position: 'prefix', decimals: 2, thousandSep: ',', decimalSep: '.' },
    evaluate: (input, config) => {
        const c = config;
        const n = parseNumber(input);
        if (n === null)
            return input === null || input === undefined ? '' : String(input);
        const formatted = formatWithSeparators(n, c.decimals, c.thousandSep ?? ',', c.decimalSep ?? '.');
        return c.position === 'prefix' ? `${c.symbol}${formatted}` : `${formatted}${c.symbol}`;
    },
    validate: (config) => {
        const c = config;
        if (typeof c.symbol !== 'string' || c.symbol.length === 0)
            return fail('symbol', 'symbol is required');
        if (!['prefix', 'suffix'].includes(c.position ?? ''))
            return fail('position', 'position must be prefix or suffix');
        if (typeof c.decimals !== 'number' || c.decimals < 0)
            return fail('decimals', 'decimals must be a non-negative integer');
        return ok();
    },
    formSchema: [
        { key: 'symbol', label: 'Currency symbol', type: 'text', required: true },
        { key: 'position', label: 'Position', type: 'select', required: true, options: [
                { value: 'prefix', label: 'Prefix ($1,000)' },
                { value: 'suffix', label: 'Suffix (1,000€)' },
            ] },
        { key: 'decimals', label: 'Decimal places', type: 'number', required: true },
        { key: 'thousandSep', label: 'Thousand separator', type: 'text' },
        { key: 'decimalSep', label: 'Decimal separator', type: 'text' },
    ],
    sampleSeeds: ['1234.5', '0', '-100'],
};
function roundValue(n, decimals, mode) {
    const factor = Math.pow(10, decimals);
    const scaled = n * factor;
    switch (mode) {
        case 'half-up': return Math.sign(scaled) * Math.round(Math.abs(scaled)) / factor;
        case 'half-even': {
            const floor = Math.floor(scaled);
            const diff = scaled - floor;
            if (diff < 0.5)
                return floor / factor;
            if (diff > 0.5)
                return (floor + 1) / factor;
            // exactly 0.5 → banker's rounding (round to even)
            return (floor % 2 === 0 ? floor : floor + 1) / factor;
        }
        case 'floor': return Math.floor(scaled) / factor;
        case 'ceil': return Math.ceil(scaled) / factor;
        default: return n;
    }
}
const round = {
    id: 'round',
    family: 'number',
    label: 'Round',
    description: 'Round to N decimals with chosen mode (half-up, half-even, floor, ceil).',
    defaultConfig: { decimals: 0, mode: 'half-up' },
    evaluate: (input, config) => {
        const c = config;
        const n = parseNumber(input);
        if (n === null)
            return input === null || input === undefined ? '' : String(input);
        return String(roundValue(n, c.decimals, c.mode));
    },
    validate: (config) => {
        const c = config;
        if (typeof c.decimals !== 'number' || c.decimals < 0)
            return fail('decimals', 'decimals must be a non-negative integer');
        if (!['half-up', 'half-even', 'floor', 'ceil'].includes(c.mode ?? ''))
            return fail('mode', 'mode must be half-up / half-even / floor / ceil');
        return ok();
    },
    formSchema: [
        { key: 'decimals', label: 'Decimal places', type: 'number', required: true },
        { key: 'mode', label: 'Mode', type: 'select', required: true, options: [
                { value: 'half-up', label: 'Half-up' },
                { value: 'half-even', label: 'Half-even (banker\'s)' },
                { value: 'floor', label: 'Floor' },
                { value: 'ceil', label: 'Ceil' },
            ] },
    ],
    sampleSeeds: ['1.23456', '2.5', '-0.5'],
};
export const numberOps = [numberFormat, currencyFormat, round];
//# sourceMappingURL=number-ops.js.map